/**
 * Drizzle ORM + IndexedDB Implementation of IIllustrationRepository
 */
import type { IIllustrationRepository, IllustrationRecord } from './IIllustrationRepository';
import { getIndexedDb, STORES } from '../db/db.web';
import { generateUUID } from '../core/uuid';

const SEED_ILLUSTRATIONS: IllustrationRecord[] = [
  {
    id: 'MH-1001-01',
    customerId: 'KH-1001',
    proposalName: 'Xanh Uớc Mơ - Gói Gia Đình An Tâm (Chính)',
    productCode: 'XUM',
    customerName: 'Hoàng Cao Minh',
    customerAge: 34,
    customerGender: 'Nam',
    sumAssured: '1.000.000.000',
    term: 20,
    freq: 'Năm',
    premium: '28.500',
    stage: 'negotiating',
    lastUpdated: '20/06/2026',
    expiryDate: '20/07/2026',
    selectedRiders: ['R1', 'R2'],
    topUpAmount: 2000000,
    createdAt: '2026-06-20T10:00:00.000Z',
    updatedAt: '2026-06-20T10:00:00.000Z',
  },
  {
    id: 'MH-1002-01',
    customerId: 'KH-1002',
    proposalName: 'Xanh Uớc Mơ - Gói Tương Lai Học Vấn',
    productCode: 'XUM',
    customerName: 'Tạ Minh Luận',
    customerAge: 29,
    customerGender: 'Nam',
    sumAssured: '500.000.000',
    term: 15,
    freq: 'Năm',
    premium: '15.200',
    stage: 'new',
    lastUpdated: '22/06/2026',
    expiryDate: '22/07/2026',
    selectedRiders: ['R1', 'R4'],
    topUpAmount: 0,
    createdAt: '2026-06-22T14:30:00.000Z',
    updatedAt: '2026-06-22T14:30:00.000Z',
  },
];

export class IllustrationRepository implements IIllustrationRepository {
  private initialized = false;
  private memoryStore: IllustrationRecord[] = [...SEED_ILLUSTRATIONS];

  private async ensureInitialized(): Promise<void> {
    if (this.initialized) return;
    try {
      const db = await getIndexedDb();
      if (db) {
        const count = await db.count(STORES.ILLUSTRATIONS);
        if (count === 0) {
          const tx = db.transaction(STORES.ILLUSTRATIONS, 'readwrite');
          for (const item of SEED_ILLUSTRATIONS) {
            await tx.store.put(item);
          }
          await tx.done;
        }
        this.initialized = true;
      }
    } catch (err) {
      // IndexedDB not available
    }
  }

  async getByCustomerId(customerId: string): Promise<IllustrationRecord[]> {
    await this.ensureInitialized();
    try {
      const db = await getIndexedDb();
      const all: IllustrationRecord[] = await db.getAll(STORES.ILLUSTRATIONS);
      return all.filter((i) => i.customerId === customerId);
    } catch (err) {
      return this.memoryStore.filter((i) => i.customerId === customerId);
    }
  }

  async getAll(): Promise<IllustrationRecord[]> {
    await this.ensureInitialized();
    try {
      const db = await getIndexedDb();
      return await db.getAll(STORES.ILLUSTRATIONS);
    } catch (err) {
      return this.memoryStore;
    }
  }

  async getById(id: string): Promise<IllustrationRecord | undefined> {
    await this.ensureInitialized();
    try {
      const db = await getIndexedDb();
      return await db.get(STORES.ILLUSTRATIONS, id);
    } catch (err) {
      return this.memoryStore.find((i) => i.id === id);
    }
  }

  async save(data: Omit<IllustrationRecord, 'id' | 'createdAt' | 'updatedAt'> & { id?: string }): Promise<IllustrationRecord> {
    await this.ensureInitialized();
    const nowIso = new Date().toISOString();

    const newRecord: IllustrationRecord = {
      ...data,
      id: data.id || generateUUID(),
      createdAt: nowIso,
      updatedAt: nowIso,
    };

    try {
      const db = await getIndexedDb();
      await db.put(STORES.ILLUSTRATIONS, newRecord);
    } catch (err) {
      this.memoryStore.push(newRecord);
    }

    return newRecord;
  }

  async update(id: string, partialData: Partial<IllustrationRecord>): Promise<IllustrationRecord | undefined> {
    await this.ensureInitialized();
    const existing = await this.getById(id);
    if (!existing) return undefined;

    const nowIso = new Date().toISOString();
    const updated: IllustrationRecord = {
      ...existing,
      ...partialData,
      updatedAt: nowIso,
    };

    try {
      const db = await getIndexedDb();
      await db.put(STORES.ILLUSTRATIONS, updated);
    } catch (err) {
      const idx = this.memoryStore.findIndex((i) => i.id === id);
      if (idx !== -1) {
        this.memoryStore[idx] = updated;
      }
    }

    return updated;
  }

  async delete(id: string): Promise<boolean> {
    await this.ensureInitialized();
    try {
      const db = await getIndexedDb();
      await db.delete(STORES.ILLUSTRATIONS, id);
      return true;
    } catch (err) {
      const idx = this.memoryStore.findIndex((i) => i.id === id);
      if (idx !== -1) {
        this.memoryStore.splice(idx, 1);
        return true;
      }
      return false;
    }
  }
}

export const illustrationRepository = new IllustrationRepository();
